import { useState } from "react";
import { api } from "../api";
import type { Account } from "../types";
import { fmtTs, todayIso } from "../utils";
import { FansChart } from "./FansChart";
import { Modal } from "./Modal";

export function AccountsPanel({
  customerId,
  accounts,
  onChanged,
  flash,
}: {
  customerId: string;
  accounts: Account[];
  onChanged: () => Promise<void>;
  flash: (kind: "info" | "error", msg: string) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  const create = async (body: Partial<Account>) => {
    try {
      await api.addAccount(customerId, body);
      setShowCreate(false);
      await onChanged();
      flash("info", "已新增账号");
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  const update = async (body: Partial<Account>) => {
    if (!editing) return;
    try {
      await api.updateAccount(customerId, editing.id, body);
      setEditing(null);
      await onChanged();
      flash("info", "已更新账号");
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  const remove = async (a: Account) => {
    if (!confirm(`真的要删除账号「${a.name}」吗？粉丝历史会一起丢失。`)) return;
    try {
      await api.removeAccount(customerId, a.id);
      await onChanged();
      flash("info", "已删除账号");
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + 新增账号
        </button>
      </div>

      {accounts.length === 0 && (
        <div className="card px-5 py-8 text-center text-sm text-ink-500">
          还没有运营账号，点击「新增账号」开始
        </div>
      )}

      {accounts.map((a) => (
        <AccountCard
          key={a.id}
          customerId={customerId}
          account={a}
          onEdit={() => setEditing(a)}
          onDelete={() => remove(a)}
          onChanged={onChanged}
          flash={flash}
        />
      ))}

      {showCreate && (
        <AccountForm
          title="新增账号"
          onClose={() => setShowCreate(false)}
          onSubmit={create}
        />
      )}
      {editing && (
        <AccountForm
          title={`编辑账号：${editing.name}`}
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={update}
        />
      )}
    </div>
  );
}

function AccountCard({
  customerId,
  account,
  onEdit,
  onDelete,
  onChanged,
  flash,
}: {
  customerId: string;
  account: Account;
  onEdit: () => void;
  onDelete: () => void;
  onChanged: () => Promise<void>;
  flash: (kind: "info" | "error", msg: string) => void;
}) {
  const [date, setDate] = useState(todayIso());
  const [fans, setFans] = useState("");
  const [fansNote, setFansNote] = useState("");

  const addPoint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fans.trim()) return;
    try {
      await api.addFansPoint(customerId, account.id, {
        date: date.trim(),
        fans: Number(fans),
        note: fansNote.trim(),
      });
      setFans("");
      setFansNote("");
      await onChanged();
    } catch (e: unknown) {
      flash("error", (e as Error).message);
    }
  };

  return (
    <section className="card overflow-hidden">
      <header className="flex items-start justify-between gap-4 px-5 py-3 border-b border-ink-100">
        <div>
          <div className="flex items-baseline gap-2">
            <h4 className="font-semibold">{account.name}</h4>
            <span className="chip">{account.platform}</span>
            {account.handle && (
              <span className="font-mono text-xs text-ink-500">{account.handle}</span>
            )}
            <span className="font-mono text-xs text-ink-400">{account.id}</span>
          </div>
          {account.url && (
            <a
              href={account.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-indigo-600 hover:underline"
            >
              {account.url}
            </a>
          )}
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={onEdit}>
            编辑
          </button>
          <button className="btn-danger" onClick={onDelete}>
            删除
          </button>
        </div>
      </header>
      {account.positioning && (
        <div className="border-b border-ink-100 bg-amber-50/50 px-5 py-3">
          <div className="mb-1 text-xs font-medium text-amber-800">账号定位</div>
          <p className="whitespace-pre-wrap text-sm text-amber-900">{account.positioning}</p>
        </div>
      )}
      {account.note && (
        <div className="border-b border-ink-100 px-5 py-2 text-sm text-ink-600">{account.note}</div>
      )}

      <div className="px-5 py-3">
        <div className="mb-2 flex items-baseline justify-between">
          <h5 className="text-sm font-medium">粉丝增长</h5>
          <span className="text-xs text-ink-500">{account.fans_history.length} 个数据点</span>
        </div>
        <FansChart history={account.fans_history} />
        <form onSubmit={addPoint} className="mt-3 grid grid-cols-[8rem_6rem_1fr_auto] gap-2">
          <input
            className="input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
          <input
            className="input"
            type="number"
            min={0}
            placeholder="粉丝数"
            value={fans}
            onChange={(e) => setFans(e.target.value)}
            required
          />
          <input
            className="input"
            placeholder="备注（可选，例如：发了第 3 条视频后）"
            value={fansNote}
            onChange={(e) => setFansNote(e.target.value)}
          />
          <button className="btn-primary" type="submit">
            记录
          </button>
        </form>
        {account.fans_history.length > 0 && (
          <details className="mt-2 text-xs text-ink-600">
            <summary className="cursor-pointer select-none">查看明细</summary>
            <ul className="mt-1 max-h-48 overflow-y-auto scrollbar-thin divide-y divide-ink-100 rounded-md border border-ink-200">
              {[...account.fans_history].reverse().map((p, i) => (
                <li key={i} className="flex justify-between px-3 py-1.5">
                  <span className="font-mono">{p.date}</span>
                  <span className="flex-1 px-3 text-ink-500">{p.note}</span>
                  <span className="font-semibold">{p.fans}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <footer className="border-t border-ink-100 bg-ink-50 px-5 py-1.5 text-xs text-ink-500">
        创建于 {fmtTs(account.created_at)}
      </footer>
    </section>
  );
}

function AccountForm({
  title,
  initial,
  onClose,
  onSubmit,
}: {
  title: string;
  initial?: Account;
  onClose: () => void;
  onSubmit: (body: Partial<Account>) => void;
}) {
  const isEdit = !!initial?.id;
  const [platform, setPlatform] = useState(initial?.platform ?? "抖音");
  const [name, setName] = useState(initial?.name ?? "");
  const [handle, setHandle] = useState(initial?.handle ?? "");
  const [url, setUrl] = useState(initial?.url ?? "");
  const [id, setId] = useState(initial?.id ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [positioning, setPositioning] = useState(initial?.positioning ?? "");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Partial<Account> = {
      platform: platform.trim(),
      name: name.trim(),
      handle: handle.trim(),
      url: url.trim(),
      note,
      positioning,
    };
    if (!isEdit && id.trim()) body.id = id.trim();
    onSubmit(body);
  };

  return (
    <Modal title={title} onClose={onClose} size="lg">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">平台 *</label>
            <input
              className="input"
              required
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              placeholder="抖音 / B 站 / 小红书 …"
            />
          </div>
          <div>
            <label className="label">账号名 *</label>
            <input
              className="input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">handle</label>
            <input
              className="input"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@zhangsan_daily"
            />
          </div>
          <div>
            <label className="label">账号 id（slug）{isEdit && "（不可改）"}</label>
            <input
              className="input"
              value={id}
              disabled={isEdit}
              onChange={(e) => setId(e.target.value)}
              placeholder="留空则按 platform-name 派生"
            />
          </div>
        </div>
        <div>
          <label className="label">主页 URL</label>
          <input
            className="input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.douyin.com/user/..."
          />
        </div>
        <div>
          <label className="label">账号定位（重点：账号要做的内容方向、人设、目标受众）</label>
          <textarea
            className="textarea h-32"
            value={positioning}
            onChange={(e) => setPositioning(e.target.value)}
            placeholder="例如：面向 25–35 岁宝妈，分享日常带娃 + 家庭料理。强调真实感而非摆拍。每周 3 条短视频，节奏稳定。"
          />
        </div>
        <div>
          <label className="label">备注</label>
          <textarea
            className="textarea h-16"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button type="submit" className="btn-primary">
            {isEdit ? "保存" : "创建"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
